import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { createImage, exportEditedImageFile } from '../utils/imageEditorExport';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const DEFAULT_ZOOM = 1;

function ImageEditorModal({ isOpen, sourceFile, onConfirm, onCancel }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [mediaSize, setMediaSize] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !sourceFile) {
      setImageSrc(null);
      setMediaSize(null);
      return undefined;
    }

    const url = URL.createObjectURL(sourceFile);
    setImageSrc(url);
    setMediaSize(null);
    setCrop({ x: 0, y: 0 });
    setZoom(DEFAULT_ZOOM);
    setRotation(0);
    setCroppedAreaPixels(null);
    setError('');
    setProcessing(false);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [isOpen, sourceFile]);

  useEffect(() => {
    if (!imageSrc) return undefined;
    let cancelled = false;
    createImage(imageSrc)
      .then((img) => {
        if (cancelled) return;
        setMediaSize({ width: img.naturalWidth, height: img.naturalHeight });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[ImageEditorModal] image load error:', err);
        setError('Görsel yüklenemedi.');
      });
    return () => {
      cancelled = true;
    };
  }, [imageSrc]);

  /** Kırpma penceresi görselin kendi en-boy oranında; zorunlu 16:9 vb. yok. */
  const cropAspect = useMemo(() => {
    if (!mediaSize) return undefined;
    const swapped = rotation % 180 !== 0;
    return swapped
      ? mediaSize.height / mediaSize.width
      : mediaSize.width / mediaSize.height;
  }, [mediaSize, rotation]);

  const onCropComplete = useCallback((_croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(DEFAULT_ZOOM);
    setRotation(0);
    setError('');
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
    setCrop({ x: 0, y: 0 });
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
    setCrop({ x: 0, y: 0 });
  };

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !sourceFile) return;
    setProcessing(true);
    setError('');
    try {
      const baseName = String(sourceFile.name || 'foto').replace(/\.[^.]+$/, '');
      const editedFile = await exportEditedImageFile(
        imageSrc,
        croppedAreaPixels,
        rotation,
        `${baseName}.jpg`,
        mediaSize
      );
      onConfirm(editedFile);
    } catch (err) {
      console.error('[ImageEditorModal] export error:', err);
      setError(err?.message || 'Görsel işlenirken bir hata oluştu.');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen || !sourceFile) {
    return null;
  }

  const cropperReady = Boolean(imageSrc && mediaSize && cropAspect);

  return (
    <div
      className="modal-overlay image-editor-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="image-editor-title"
      onClick={onCancel}
    >
      <div
        className="modal-content image-editor-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id="image-editor-title">Görseli düzenle</h2>
          <button
            type="button"
            className="modal-close-btn"
            onClick={onCancel}
            aria-label="Kapat"
            disabled={processing}
          >
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <div className="image-editor-modal__body">
          <p className="image-editor-modal__hint">
            Görsel, yüklendiği en-boy oranında kaydedilir; zorunlu bir format yoktur. Kenarlardan
            kırpmak için yakınlaştırıp sürükleyin.
          </p>

          <div className="image-editor-modal__crop-area">
            {cropperReady ? (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={cropAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                objectFit="contain"
                showGrid
              />
            ) : (
              <div className="image-editor-modal__loading">
                <i className="fas fa-spinner fa-spin" aria-hidden />
                <span>Görsel yükleniyor…</span>
              </div>
            )}
          </div>

          <div className="image-editor-modal__toolbar">
            <div className="image-editor-modal__control">
              <label htmlFor="image-editor-zoom">
                <i className="fas fa-search-plus" aria-hidden />
                Yakınlaştır
              </label>
              <input
                id="image-editor-zoom"
                type="range"
                min={MIN_ZOOM}
                max={MAX_ZOOM}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                disabled={processing || !cropperReady}
              />
              <span className="image-editor-modal__control-value">{Math.round(zoom * 100)}%</span>
            </div>

            <div className="image-editor-modal__actions">
              <button
                type="button"
                className="secondary-btn image-editor-modal__icon-btn"
                onClick={handleRotateLeft}
                disabled={processing || !cropperReady}
                title="Sola döndür"
              >
                <i className="fas fa-undo" aria-hidden />
                Sola
              </button>
              <button
                type="button"
                className="secondary-btn image-editor-modal__icon-btn"
                onClick={handleRotateRight}
                disabled={processing || !cropperReady}
                title="Sağa döndür"
              >
                <i className="fas fa-redo" aria-hidden />
                Sağa
              </button>
              <button
                type="button"
                className="secondary-btn image-editor-modal__icon-btn"
                onClick={handleReset}
                disabled={processing || !cropperReady}
                title="Sıfırla"
              >
                <i className="fas fa-sync-alt" aria-hidden />
                Sıfırla
              </button>
            </div>
          </div>

          {error && (
            <div className="form-error image-editor-modal__error">
              <i className="fas fa-exclamation-circle" aria-hidden />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="secondary-btn"
            onClick={onCancel}
            disabled={processing}
          >
            İptal
          </button>
          <button
            type="button"
            className="primary-btn"
            onClick={handleConfirm}
            disabled={processing || !croppedAreaPixels || !cropperReady}
          >
            {processing ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden />
                İşleniyor…
              </>
            ) : (
              <>
                <i className="fas fa-check" aria-hidden />
                Onayla
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageEditorModal;
